"use client";

import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Fish, Shield, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const equipmentData = [{
  id: "eq-1",
  name: "M365 Business Premium",
  type: "Core License",
  bestFor: "$26.40 (Market Avg)",
  durability: "$22.00 (Direct)",
  link: "/TransparencyPage",
  proTip: true,
  icon: Fish
}, {
  id: "eq-2",
  name: "M365 Business Standard",
  type: "Core License",
  bestFor: "$15.00 (Market Avg)",
  durability: "$12.50 (Direct)",
  link: "/TransparencyPage",
  proTip: true,
  icon: Fish
}, {
  id: "eq-3",
  name: "Fractional IT Support",
  type: "Managed Service",
  bestFor: "$150.00+ (Variable)",
  durability: "$110.00 (Flat)",
  link: "/ThePartnershipPage",
  proTip: false,
  icon: Info
}, {
  id: "eq-4",
  name: "Defender for Business",
  type: "Security License",
  bestFor: "$3.60 (Market Avg)",
  durability: "$3.00 (Direct)",
  link: "/TransparencyPage",
  proTip: true,
  icon: Shield
}, {
  id: "eq-5",
  name: "O365 Tenant Management",
  type: "Service Bundle",
  bestFor: "$15.00 (Add-on Fee)",
  durability: "Included",
  link: "/HomePage",
  proTip: true,
  icon: Shield
}];
export default function EquipaggiamentoPro() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const tableReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224648" id="zero-markup-philosophy" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[600px] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div ref={headerReveal.ref} className={`mb-16 text-center transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 uppercase tracking-widest font-default">
            Pricing Transparency
          </Badge>
          <h2 className="font-default text-3xl md:text-5xl font-bold mb-6 text-foreground tracking-tight">
            Zero-Markup Philosophy
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed font-default">
            We believe in radical transparency. While others hide margins in licensing, we pass through direct costs so your startup can scale efficiently without hidden overhead.
          </p>
        </div>

        <div ref={tableReveal.ref} className={`transition-all duration-1000 delay-200 ${tableReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="rounded-xl border border-border/40 bg-card/40 backdrop-blur-xl shadow-2xl overflow-hidden">
            <div className="max-h-[600px] overflow-auto custom-scrollbar">
              <Table>
                <TableHeader className="bg-card/90 backdrop-blur-md sticky top-0 z-20 shadow-sm">
                  <TableRow className="border-border/40 hover:bg-transparent">
                    <TableHead className="w-[350px] font-default text-foreground/90 h-14 uppercase text-xs tracking-wider">
                      License / Service
                    </TableHead>
                    <TableHead className="font-default text-foreground/90 h-14 uppercase text-xs tracking-wider">
                      Typical Market Rate
                    </TableHead>
                    <TableHead className="font-default text-foreground/90 h-14 uppercase text-xs tracking-wider">
                      SMB IT Direct Cost
                    </TableHead>
                    <TableHead className="text-right font-default text-foreground/90 h-14 pr-6 uppercase text-xs tracking-wider">
                      Details
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {equipmentData.map((item, i) => {
                  const Icon = item.icon;
                  return <TableRow key={item.id} data-index={i} className="border-border/20 hover:bg-accent/10 transition-colors group">
                        <TableCell className="font-medium py-4">
                          <div className="flex items-center gap-4">
                            <div className="p-2.5 rounded-lg bg-secondary/60 text-primary shadow-inner border border-white/5">
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-foreground font-default font-semibold tracking-tight">
                                {item.name}
                              </span>
                              <span className="text-xs text-muted-foreground mt-0.5 font-mono">
                                {item.type}
                              </span>
                            </div>
                            {item.proTip && <Badge variant="default" className="ml-2 bg-emerald-500/15 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/25 shadow-none font-default text-[10px] uppercase">
                                Zero Markup
                              </Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground/60 py-4 font-mono line-through">
                          {item.bestFor}
                        </TableCell>
                        <TableCell className="py-4">
                          <Badge variant="outline" className="border-emerald-500/30 text-emerald-400 bg-emerald-500/5 font-mono px-3 py-1">
                            {item.durability}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right py-4 pr-6">
                          <Button variant="ghost" size="sm" asChild className="opacity-0 group-hover:opacity-100 transition-opacity text-primary hover:text-primary hover:bg-primary/10 font-default">
                            <Link to={item.link}>
                              View <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>;
                })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </section>;
}